import React, { useEffect, useRef, useState } from "react";
import ConfirmDialog from "./ConfirmDialog";
import * as LocalStore from "../services/LocalStore";
import { useToast } from "./ToastProvider";

type Calendar = {
  name: string;
  year: number;
  type: string;
  listOfDays?: string[];
};

export default function Calendars() {
  const toast = useToast();
  const [items, setItems] = useState<Calendar[]>([]);
  const [name, setName] = useState("");
  const [type, setType] = useState("");
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [originalName, setOriginalName] = useState<string | null>(null);

  useEffect(() => {
    setSelected(
      new Set(Array.from(selected).filter((s) => s.startsWith(`${year}-`)))
    );
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year]);

  async function load() {
    const data = await LocalStore.getCalendars();
    setItems(data ?? []);
  }

  function iso(y: number, m: number, d: number) {
    const mm = String(m).padStart(2, "0");
    const dd = String(d).padStart(2, "0");
    return `${y}-${mm}-${dd}`;
  }

  const lastAnchorKey = useRef<string | null>(null);

  function selectRange(aKey: string, bKey: string) {
    const [ay, am, ad] = aKey.split("-").map(Number);
    const [by, bm, bd] = bKey.split("-").map(Number);
    let a = new Date(ay, am - 1, ad);
    let b = new Date(by, bm - 1, bd);
    if (a > b) {
      const t = a;
      a = b;
      b = t;
    }

    const yr = Number(year);
    const copy = new Set(selected);
    for (let d = new Date(a); d <= b; d.setDate(d.getDate() + 1)) {
      if (d.getFullYear() !== yr) continue;
      const key = iso(d.getFullYear(), d.getMonth() + 1, d.getDate());
      copy.add(key);
    }
    setSelected(copy);
  }

  function onDayClick(key: string, e: React.MouseEvent<HTMLButtonElement>) {
    const y = Number(year);
    if (
      e.shiftKey &&
      lastAnchorKey.current &&
      lastAnchorKey.current.startsWith(`${y}-`)
    ) {
      selectRange(lastAnchorKey.current, key);
    } else {
      const copy = new Set(selected);
      if (copy.has(key)) copy.delete(key);
      else copy.add(key);
      setSelected(copy);
      if (!e.shiftKey) lastAnchorKey.current = key;
    }
  }

  function selectDowForMonth(y: number, m: number, targetDow: number) {
    const daysInMonth = new Date(y, m, 0).getDate();
    const dayList: string[] = [];
    for (let day = 1; day <= daysInMonth; day++) {
      const dt = new Date(y, m - 1, day);
      if (dt.getDay() !== targetDow) continue;
      const key = iso(y, m, day);
      dayList.push(key);
    }

    const copy = new Set(selected);
    if (dayList.every((d) => copy.has(d))) {
      dayList.forEach((k) => copy.delete(k));
    } else {
      dayList.forEach((k) => copy.add(k));
    }
    setSelected(copy);
  }

  function updateSelectedCount() {
    return Array.from(selected).filter((s) => s.startsWith(`${year}-`)).length;
  }

  async function onSave(e?: React.FormEvent) {
    if (e) e.preventDefault();
    if (!type) {
      toast.show("Please select a calendar type.", "error");
      return;
    }
    const trimmed = name.trim();
    if (!trimmed) {
      toast.show("Please enter a name.", "error");
      return;
    }
    // If we're creating a new calendar (not editing an existing one), prevent duplicate names
    if (!originalName) {
      const exists = await LocalStore.calendarExists(trimmed);
      if (exists) {
        toast.show(
          "A calendar with that name already exists. Please choose a different name.",
          "error"
        );
        return;
      }
    }
    const payload = {
      name: trimmed,
      year: Number(year),
      listOfDays: Array.from(selected),
      type,
    };
    const saved = await LocalStore.saveCalendar(payload);
    if (!saved) toast.show("Save failed", "error");
    else {
      setName("");
      setType("");
      setSelected(new Set());
      setOriginalName(null);
      await load();
    }
  }

  async function onEdit(c: Calendar) {
    setName(c.name);
    setYear(String(c.year));
    setType(c.type);
    setSelected(
      new Set((c.listOfDays ?? []).map((d) => String(d).slice(0, 10)))
    );
    setOriginalName(c.name);
  }

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toDelete, setToDelete] = useState<Calendar | null>(null);

  function requestDelete(c: Calendar) {
    setToDelete(c);
    setConfirmOpen(true);
  }

  async function doDelete() {
    if (!toDelete) return;
    const ok = await LocalStore.deleteCalendar(toDelete.name);
    setConfirmOpen(false);
    setToDelete(null);
    if (ok) await load();
    else toast.show("Delete failed", "error");
  }

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  function renderMonth(y: number, m: number) {
    const first = new Date(y, m - 1, 1);
    const daysInMonth = new Date(y, m, 0).getDate();
    const offset = first.getDay();
    const cells: Array<React.ReactNode> = [];
    for (let i = 0; i < offset; i++)
      cells.push(<div key={`b${i}`} className="cell empty" />);
    for (let d = 1; d <= daysInMonth; d++) {
      const key = iso(y, m, d);
      const isSel = selected.has(key);
      cells.push(
        <div key={key} className="cell">
          <button
            type="button"
            className="day"
            aria-pressed={isSel}
            data-date={key}
            onClick={(e) => onDayClick(key, e)}
          >
            {d}
          </button>
        </div>
      );
    }
    return (
      <section key={`${y}-${m}`} className="month">
        <h3>
          {monthNames[m - 1]} {y}
        </h3>
        <div className="grid">
          {["U", "M", "T", "W", "R", "F", "S"].map((lbl, i) => (
            <div
              key={lbl}
              className="dow"
              title={`Select all ${lbl} in ${monthNames[m - 1]}`}
              onClick={() => selectDowForMonth(y, m, i)}
            >
              {lbl}
            </div>
          ))}
          {cells}
        </div>
      </section>
    );
  }

  return (
    <div>
      <h1>Calendars</h1>
      <h2>Existing</h2>
      <table className="existing-table" aria-label="Existing calendars">
        <thead>
          <tr>
            <th>Name</th>
            <th>Year</th>
            <th>Type</th>
            <th className="num">Days</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {items.map((c) => (
            <tr key={c.name}>
              <td>{c.name}</td>
              <td>{c.year}</td>
              <td>{c.type}</td>
              <td className="num">{c.listOfDays?.length ?? 0}</td>
              <td>
                <button className="action-btn" onClick={() => onEdit(c)}>
                  Edit
                </button>
                <button
                  className="action-btn danger"
                  onClick={() => requestDelete(c)}
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <hr />
      <form id="calForm" className="cal-form" onSubmit={onSave}>
        <div className="form-grid">
          <div className="field">
            <label htmlFor="name">Name</label>
            <input
              id="name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="type">Type</label>
            <select
              id="type"
              required
              value={type}
              onChange={(e) => setType(e.target.value)}
            >
              <option value="">Select a Type</option>
              <option value="WorkDays">Work Days</option>
              <option value="NonWorkDays">Non Work Days</option>
            </select>
          </div>

          <div className="field">
            <label htmlFor="year">Year</label>
            <input
              id="year"
              type="number"
              min={2000}
              max={2100}
              required
              value={year}
              onChange={(e) => {
                setYear(e.target.value);
              }}
              disabled={updateSelectedCount() > 0}
              title={
                updateSelectedCount() > 0
                  ? "Add a new Calendar to cover additional years"
                  : ""
              }
            />
          </div>
        </div>

        <div className="row controls">
          <button type="submit">Save</button>
          <span className="pill">
            <span id="selCount">{updateSelectedCount()}</span> days selected
          </span>
        </div>

        <div id="yearCalendar" className="calendar" aria-label="Year calendar">
          {Array.from({ length: 12 }, (_, i) =>
            renderMonth(Number(year), i + 1)
          )}
        </div>
      </form>
      <ConfirmDialog
        open={confirmOpen}
        title={`Delete calendar "${toDelete?.name}"?`}
        onConfirm={doDelete}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
}
